// First-party notice and purpose controls. The identity SDK remains the sole
// authority for consent; opening/dismissing this UI never grants a purpose.
const { isTopLevelContext, isSameSiteWithIssuer } = require('./context');
const { getPrivacyPolicyUrl } = require('../../brandConfigs');

const TEXT = {
  en: {
    title: 'Your privacy choices', launcher: 'Privacy choices', close: 'Close', save: 'Save choices', saving: 'Saving…',
    notice: 'We use a first-party identifier on this site to keep your reading history with the account you may create, and a separate advertising identifier so ads can be capped and measured. You can turn either off at any time.',
    policy: 'Privacy policy',
    reading: 'Reading continuity', ads: 'Advertising identifier',
    readingHelp: 'Turning this off removes the reading history associated with this browser’s identifier.',
    adsHelp: 'Allow an advertising identifier to limit repeated ads and measure campaigns. Ads may still appear when this is off.',
    regional: 'Your consent-banner choices also apply. Turning a control on here does not override them.',
    failure: 'Your choices could not be updated. Please try again.',
    erasureFailure: 'Reading continuity is off, but we could not finish deleting its history. Please try again.',
  },
  fr: {
    title: 'Vos choix de confidentialité', launcher: 'Confidentialité', close: 'Fermer', save: 'Enregistrer mes choix', saving: 'Enregistrement…',
    notice: 'Nous utilisons un identifiant de première partie sur ce site pour relier votre historique de lecture au compte que vous pourriez créer, ainsi qu’un identifiant publicitaire distinct pour limiter la répétition des publicités et en mesurer la diffusion. Vous pouvez désactiver l’un ou l’autre à tout moment.',
    policy: 'Politique de confidentialité',
    reading: 'Continuité de lecture', ads: 'Identifiant publicitaire',
    readingHelp: 'La désactivation supprime l’historique de lecture associé à l’identifiant de ce navigateur.',
    adsHelp: 'Permettre un identifiant publicitaire pour limiter les publicités répétées et mesurer les campagnes. Des publicités peuvent toujours apparaître si cette option est désactivée.',
    regional: 'Vos choix dans la bannière de consentement s’appliquent aussi. Activer une option ici ne les remplace pas.',
    failure: 'Vos choix n’ont pas pu être mis à jour. Veuillez réessayer.',
    erasureFailure: 'La continuité de lecture est désactivée, mais la suppression de son historique n’a pas pu être terminée. Veuillez réessayer.',
  },
};

let mounted = null;

function unmount() {
  if (!mounted) return;
  const { api, update, host } = mounted;
  api.off('identity:ready', update);
  api.off('identity:ads', update);
  host.remove();
  mounted = null;
}

function mount(api, config = {}) {
  if (typeof document === 'undefined' || !document.body || typeof document.createElement !== 'function') return;
  if (config.identity === false || !isTopLevelContext() || !isSameSiteWithIssuer(config.issuer)) { unmount(); return; }
  if (mounted) return;
  const language = String(document.documentElement.lang || navigator.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const t = TEXT[language];
  const policyUrl = getPrivacyPolicyUrl(config.issuer, language);
  const host = document.createElement('div');
  host.id = 'milla-privacy-controls';
  const root = host.attachShadow({ mode: 'open' });
  root.innerHTML = `<style>
    :host { all:initial; font:14px/1.5 ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; color:#111827; }
    * { box-sizing:border-box; } :host([hidden]),[hidden] { display:none!important; }
    button,input { font:inherit; } button { cursor:pointer; } button:disabled { cursor:wait; opacity:.65; }
    button:focus-visible,input:focus-visible,a:focus-visible { outline:3px solid #2563eb; outline-offset:3px; }
    a { color:inherit; }
    dialog { position:fixed; inset:0; margin:auto; width:calc(100vw - 24px); max-width:420px; max-height:calc(100dvh - 32px); overflow:auto; border:1px solid #e5e7eb; border-radius:16px; background:#fff; color:#111827; padding:22px; box-shadow:0 12px 40px #0003; font:inherit; }
    dialog::backdrop { background:#11182755; }
    h2 { margin:0 32px 12px 0; font-size:18px; line-height:1.3; } p { margin:0 0 16px; }
    .close { position:absolute; top:12px; right:12px; border:0; background:transparent; color:#374151; width:32px; height:32px; font-size:24px; }
    .choice { border-top:1px solid #e5e7eb; padding:14px 0; } label { display:flex; gap:14px; justify-content:space-between; align-items:center; font-weight:600; cursor:pointer; }
    input { width:20px; height:20px; flex:none; accent-color:#111827; }
    .help { font-size:12px; line-height:1.5; color:#4b5563; margin:6px 0 0; }
    .regional { font-size:12px; color:#4b5563; } .error { color:#b91c1c; font-size:13px; }
    .save { width:100%; border:0; border-radius:9px; background:#111827; color:#fff; padding:11px 16px; font-weight:600; }
  </style>
  <dialog aria-labelledby="privacy-title" aria-describedby="privacy-notice" lang="${language}">
    <button class="close" type="button" aria-label="${t.close}">×</button>
    <h2 id="privacy-title">${t.title}</h2><p id="privacy-notice">${t.notice} <a class="policy" href="${policyUrl}" target="_blank" rel="noopener">${t.policy}</a></p>
    <form>
      <div class="choice"><label for="reading">${t.reading}<input id="reading" type="checkbox" aria-describedby="reading-help"></label><p id="reading-help" class="help">${t.readingHelp}</p></div>
      <div class="choice"><label for="ads">${t.ads}<input id="ads" type="checkbox" aria-describedby="ads-help"></label><p id="ads-help" class="help">${t.adsHelp}</p></div>
      <p class="regional">${t.regional}</p><p class="error" role="alert" hidden></p>
      <button class="save" type="submit">${t.save}</button>
    </form>
  </dialog>`;
  const dialog = root.querySelector('dialog');
  const reading = root.querySelector('#reading');
  const ads = root.querySelector('#ads');
  const save = root.querySelector('.save');
  const error = root.querySelector('.error');
  let busy = false;
  let opener = null;
  const sync = () => { reading.checked = !api.identity.isOptedOut(); ads.checked = !api.identity.ads.isOptedOut(); };
  const open = () => {
    if (busy) return;
    opener = document.activeElement;
    sync(); error.hidden = true;
    if (dialog.open) dialog.close();
    dialog.showModal();
  };
  const close = () => {
    if (busy) return;
    dialog.close();
    const target = opener?.isConnected ? opener : document.querySelector('#milla-auth-bar [data-action=privacy]');
    target?.focus();
  };
  root.querySelector('.close').addEventListener('click', close);
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  root.querySelector('form').addEventListener('submit', async (event) => {
    event.preventDefault(); if (busy) return;
    busy = true; reading.disabled = ads.disabled = save.disabled = true; save.textContent = t.saving; error.hidden = true;
    const readingChanged = reading.checked === api.identity.isOptedOut();
    const adsChanged = ads.checked === api.identity.ads.isOptedOut();
    const withdrawalWasPending = api.identity.getStatus().erasurePending;
    try {
      // A save changes only controls the reader actually edited. In particular
      // an ads-only change can never withdraw reading continuity, or vice versa.
      if (readingChanged || withdrawalWasPending) {
        const status = await api.identity[reading.checked ? 'optIn' : 'optOut']();
        if (status.erasurePending) throw new Error('withdrawal_pending');
      }
      if (adsChanged) await api.identity.ads[ads.checked ? 'optIn' : 'optOut']();
      if (readingChanged || adsChanged || withdrawalWasPending) window.location.reload();
      else { busy = false; close(); }
    } catch (_) { error.textContent = api.identity.getStatus().erasurePending ? t.erasureFailure : t.failure; error.hidden = false; }
    finally { busy = false; reading.disabled = ads.disabled = save.disabled = false; save.textContent = t.save; }
  });
  const update = () => {
    const status = api.identity.getStatus();
    if (status.identityState === 'DISABLED') { host.hidden = true; if (dialog.open) dialog.close(); return; }
    host.hidden = false;
    // Identity/consent updates never open the dialog. Readers open it through
    // the launcher or an explicit openPrivacyChoices() action.
  };
  mounted = { api, update, host, open, launcherLabel: t.launcher };
  api.on('identity:ready', update); api.on('identity:ads', update);
  document.body.appendChild(host);
  update();
}

function open() { if (mounted && !mounted.host.hidden) mounted.open(); }

function getLauncherLabel() { return mounted && !mounted.host.hidden ? mounted.launcherLabel : null; }

module.exports = { mount, unmount, open, getLauncherLabel };
